import type { SttHistoryItem } from '@/types/api';
import type { CaptionEntry } from '@/types/store';
import { toAbsoluteProfileUrl } from '@/utils/profileUtils';

const DATE_SUFFIX_REGEXP = /\s*\(DATE:\s*([^)]*)\)\s*$/;

const SYSTEM_TEXT_TO_CODE: Record<string, string> = {
  '실시간 자막이 시작되었습니다.': 'SUB_MODE_ON',
  '실시간 자막이 종료되었습니다.': 'SUB_MODE_OFF',
  '실시간 번역이 시작되었습니다.': 'TRANS_MODE_ON',
  '실시간 번역이 종료되었습니다.': 'TRANS_MODE_OFF',
};

const parseUserInfo = (userInfo: string | undefined) => {
  const value = userInfo ?? '';
  const match = value.match(/^(.*)\((.*)\)$/);
  if (!match) return { name: value, id: '' };
  return {
    name: match[1].trim(),
    id: match[2].trim(),
  };
};

const stripDateSuffix = (message: string | undefined) => {
  const value = message ?? '';
  const match = value.match(DATE_SUFFIX_REGEXP);
  if (!match) return { text: value, dateText: undefined };
  return {
    text: value.replace(DATE_SUFFIX_REGEXP, '').trim(),
    dateText: match[1]?.trim(),
  };
};

const parseTimestamp = (value: number | string | undefined): number | null => {
  if (value == null || value === '') return null;

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  if (/^\d+$/.test(value)) {
    const timestamp = Number(value);
    return Number.isFinite(timestamp) ? timestamp : null;
  }

  const koreanDateMatch = value.match(/^(\d{4})[.\-/]\s*(\d{1,2})[.\-/]\s*(\d{1,2})\.?\s+(\d{1,2}):(\d{1,2})(?::(\d{1,2}))?$/);
  if (koreanDateMatch) {
    const [, year, month, day, hour, minute, second = '0'] = koreanDateMatch;
    const timestamp = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
    ).getTime();
    return Number.isNaN(timestamp) ? null : timestamp;
  }

  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
};

const parseVoiceText = (message: string) => {
  try {
    const parsed = JSON.parse(message) as Record<string, unknown>;
    if (!parsed || typeof parsed !== 'object') {
      return { text: message, multiLangData: null, nationality: undefined };
    }

    const multiLangData: Record<string, string> = {};
    Object.entries(parsed).forEach(([key, value]) => {
      if (typeof value === 'string') {
        multiLangData[key] = value;
      }
    });

    const origin = multiLangData.origin || 'ko';
    const text = multiLangData[origin]
      || Object.entries(multiLangData).find(([key]) => key !== 'origin')?.[1]
      || message;

    return {
      text,
      multiLangData: Object.keys(multiLangData).length > 0 ? multiLangData : null,
      nationality: origin,
    };
  } catch {
    return { text: message, multiLangData: null, nationality: undefined };
  }
};

const detectSystemMessage = (message: string, explicitCode?: string | null) => {
  if (explicitCode) return { code: explicitCode, speakerName: undefined };
  if (SYSTEM_TEXT_TO_CODE[message]) return { code: SYSTEM_TEXT_TO_CODE[message], speakerName: undefined };

  const startMatch = message.match(/^(.+)님의 발언이 기록됩니다\.$/);
  if (startMatch) return { code: 'START_STT', speakerName: startMatch[1] };

  const endMatch = message.match(/^(.+)님의 발언이 더이상 기록되지 않습니다\.$/);
  if (endMatch) return { code: 'END_STT', speakerName: endMatch[1] };

  return { code: undefined, speakerName: undefined };
};

export const mapSttHistoryToCaptionEntries = (items: SttHistoryItem[]): CaptionEntry[] => {
  return items.map((item, index) => {
    const legacyUser = parseUserInfo(item.user_info);
    const strippedMessage = stripDateSuffix(item.message);
    const timestamp = parseTimestamp(item.timestamp)
      ?? parseTimestamp(strippedMessage.dateText)
      ?? Date.now() + index;
    const rawMessage = item.rawMessage ?? item.text ?? strippedMessage.text;
    const explicitSystemCode = item.systemMessage ?? (item.type === 'system' ? rawMessage : undefined);
    const detectedSystem = detectSystemMessage(item.text ?? strippedMessage.text, explicitSystemCode);
    const isSystem = item.is_system === true || item.type === 'system' || !!detectedSystem.code;
    const parsedVoiceText = isSystem ? null : parseVoiceText(item.text ?? strippedMessage.text);
    const userId = item.userId
      || item.portalId
      || item.jitsiId
      || legacyUser.id
      || `history-user-${index}`;
    const userName = detectedSystem.speakerName
      || item.userName
      || legacyUser.name
      || item.nickname
      || userId;

    return {
      id: item.id || `history-${item.type || 'voice'}-${userId}-${timestamp}-${index}`,
      userId,
      userName,
      nickname: item.nickname ?? '',
      profileUrl: toAbsoluteProfileUrl(item.profileUrl ?? item.profile_url ?? undefined) ?? '',
      text: isSystem ? (item.text ?? strippedMessage.text) : (parsedVoiceText?.text ?? strippedMessage.text),
      timestamp,
      isFinal: true,
      isSystem,
      systemMessage: isSystem ? detectedSystem.code : undefined,
      nationality: item.nationality ?? parsedVoiceText?.nationality,
      multiLangData: item.multiLangData ?? parsedVoiceText?.multiLangData ?? null,
    } satisfies CaptionEntry;
  });
};
